import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CalendarRange, Copy, Download } from "lucide-react";
import IagoSpinner from "../iago/IagoSpinner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { api } from "../../lib/api";
import { downloadFile } from "../../lib/utils";
import type { WeeklyDigest } from "../../types";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function DigestModal({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [digest, setDigest] = useState<WeeklyDigest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function generate() {
    setLoading(true);
    setError(null);
    setDigest(null);
    try {
      const d = await api.proactiveWeekly();
      setDigest(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function doCopy() {
    if (!digest) return;
    navigator.clipboard.writeText(digest.markdown).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function doDownload() {
    if (!digest) return;
    const date = new Date().toISOString().slice(0, 10);
    downloadFile(`onboard-digest-${date}.md`, digest.markdown, "text/markdown");
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-md" onClick={onClose} />
          <motion.div
            className="glass relative flex h-full w-full flex-col overflow-hidden rounded-none shadow-2xl md:h-[85vh] md:w-[780px] md:max-w-[95vw] md:rounded-2xl"
            initial={{ scale: 0.96, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.97, y: 6, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
          >
            <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500">
                  <CalendarRange className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-100">Weekly Digest</h2>
                  <p className="text-[11px] text-slate-500">Resumo da última semana gerado por IA</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {digest && (
                  <>
                    <button
                      onClick={doCopy}
                      className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-slate-300 hover:bg-white/5"
                      title="Copiar markdown"
                    >
                      <Copy className="h-3.5 w-3.5" /> {copied ? "copiado!" : "Copiar"}
                    </button>
                    <button
                      onClick={doDownload}
                      className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-slate-300 hover:bg-white/5"
                      title="Baixar .md"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-slate-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
              {!digest && !loading && !error && (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <CalendarRange className="h-12 w-12 text-violet-400/40" />
                  <div>
                    <h3 className="text-base font-semibold text-slate-100">Gerar relatório da semana</h3>
                    <p className="mt-1 max-w-md text-sm text-slate-400">
                      A IA analisa cards concluídos, criados e ativos nos últimos 7 dias, destaca o que foi entregue e
                      sugere o plano da próxima semana.
                    </p>
                  </div>
                  <button
                    onClick={generate}
                    className="flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 px-4 py-2 text-sm font-medium text-white shadow-[0_0_20px_rgba(139,92,246,0.4)] transition hover:shadow-[0_0_28px_rgba(139,92,246,0.6)]"
                  >
                    <CalendarRange className="h-4 w-4" /> Gerar digest
                  </button>
                </div>
              )}

              {loading && (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
                  <IagoSpinner size={48} />
                  <p className="text-sm">Revisando a sua semana com calma.</p>
                </div>
              )}

              {error && (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <p className="max-w-md rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-200">
                    {error}
                  </p>
                  <button
                    onClick={generate}
                    className="rounded-lg bg-white/10 px-4 py-2 text-sm text-slate-100 hover:bg-white/15"
                  >
                    Tentar novamente
                  </button>
                </div>
              )}

              {digest && (
                <div>
                  <div className="mb-4 flex flex-wrap gap-2 text-[11px] text-slate-400">
                    <span className="rounded-md bg-white/5 px-2 py-0.5">
                      {digest.stats.completed} concluídos
                    </span>
                    <span className="rounded-md bg-white/5 px-2 py-0.5">
                      {digest.stats.created} criados
                    </span>
                    <span className="rounded-md bg-white/5 px-2 py-0.5">
                      {digest.stats.active} ativos
                    </span>
                  </div>
                  <div className="prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{digest.markdown}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
